import { HttpError, post } from './http';

export interface RegisterClientPayload {
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    phone: string;
    referralCode?: string;
    brandId: number;
}

/** The API's `AnnotatedResults` envelope around the created account. */
export interface RegisterClientResponse {
    data: {
        email: string;
    };
}

/** The request fields a 400 can name, and so the ones the form can show an error against. */
export const REGISTER_CLIENT_FIELDS = [
    'firstName',
    'lastName',
    'companyName',
    'email',
    'phone',
    'referralCode'
] as const;

export type RegisterClientField = (typeof REGISTER_CLIENT_FIELDS)[number];

/** Client referral codes are exactly five digits (`tblUser.nReferralCode`). */
export const REFERRAL_CODE_LENGTH = 5;

/**
 * The lowest brand a client can register on. Brand 1 is the platform brand, and the API refuses anything
 * below 2 with a 400 on `brandId` (API-507).
 */
export const MINIMUM_BRAND_ID = 2;

/** Whether the host's brand can take a client registration at all. False for a missing brand id. */
export function canRegisterOnBrand(brandId: number | null | undefined): brandId is number {
    return typeof brandId === 'number' && Number.isInteger(brandId) && brandId >= MINIMUM_BRAND_ID;
}

/**
 * Whether a failed registration means the site cannot take registrations, rather than something the
 * visitor typed: a 404 (the brand does not exist or is not active) or a 400 naming `brandId`.
 */
export function isRegistrationUnavailable(error: unknown): boolean {
    if (!(error instanceof HttpError)) return false;
    if (error.status === 404) return true;
    if (error.status !== 400) return false;

    const body = error.cause;
    const errors =
        body !== null && typeof body === 'object'
            ? (body as Record<string, unknown>).errors
            : undefined;
    return (
        errors !== null &&
        typeof errors === 'object' &&
        !Array.isArray(errors) &&
        'brandId' in errors
    );
}

/**
 * `POST clients/register` (API-507): creates a client account on `brandId` and emails a link to set
 * a password. Anonymous, v3 only. Resolves to the registered email.
 *
 * Failures arrive as `HttpError`:
 * - 400: `errors` keyed by request field, each an array of messages. A 400 on `brandId` (missing, or
 *   below 2) is not something the visitor can fix; see `registrationFieldErrors`.
 * - 404: the brand does not exist or is not active.
 * - 409: the email is already registered.
 */
export async function registerClient(payload: RegisterClientPayload): Promise<string> {
    const response = await post<RegisterClientResponse, RegisterClientPayload>(
        'clients/register',
        payload
    );
    return response.data?.data?.email ?? payload.email;
}

/**
 * The per-field messages from a 400, limited to fields the form shows. Keys outside the form
 * (`brandId`, or anything the API adds later) are dropped, so an empty result means the caller
 * should show its generic failure instead.
 */
export function registrationFieldErrors(
    error: unknown
): Partial<Record<RegisterClientField, string>> {
    if (!(error instanceof HttpError) || error.status !== 400) return {};

    const body = error.cause;
    const errors =
        body !== null && typeof body === 'object'
            ? (body as Record<string, unknown>).errors
            : undefined;
    if (errors === null || typeof errors !== 'object' || Array.isArray(errors)) return {};

    const result: Partial<Record<RegisterClientField, string>> = {};
    for (const field of REGISTER_CLIENT_FIELDS) {
        const value = (errors as Record<string, unknown>)[field];
        const message = Array.isArray(value)
            ? value.filter((part): part is string => typeof part === 'string').join(' ')
            : typeof value === 'string'
              ? value
              : '';
        if (message) result[field] = message;
    }
    return result;
}
